import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reputationService } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  User,
  Target,
  AlertTriangle,
  Award,
  MessageSquare,
  Shield,
} from 'lucide-react';

const Onboarding = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    entity_type: '',
    public_name: '',
    aliases: [],
    country_targets: ['FR'],
    language_targets: ['fr'],
    objective: '',
    secondary_objectives: [],
    target_audiences: [],
    tone: 'sobre, crédible, factuel',
    constraints: '',
    reputation_risks: [],
    achievements: [],
  });

  const [tempInput, setTempInput] = useState('');

  const totalSteps = 5;

  const entityTypes = [
    { value: 'person', label: 'Personne physique', icon: User },
    { value: 'company', label: 'Entreprise', icon: Shield },
    { value: 'public_figure', label: 'Personnalité publique', icon: User },
    { value: 'expert', label: 'Expert / Consultant', icon: Award },
    { value: 'association', label: 'Association', icon: Target },
  ];

  const objectives = [
    'Visibilité accrue',
    'Crédibilité professionnelle',
    'Recrutement',
    'Levée de fonds',
    'Nettoyage SERP',
    'Lancement de livre',
    'Préparation médiatique',
    'Réparation d\'image',
    'Défense contre controverse',
  ];

  const audiences = [
    'Recruteurs',
    'Investisseurs',
    'Clients',
    'Journalistes',
    'Électeurs',
    'Partenaires',
    'Institutions',
    'Grand public',
  ];

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleAddItem = (field, value) => {
    if (value.trim()) {
      setFormData({
        ...formData,
        [field]: [...formData[field], value.trim()],
      });
      setTempInput('');
    }
  };

  const handleRemoveItem = (field, index) => {
    setFormData({
      ...formData,
      [field]: formData[field].filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const profile = await reputationService.createProfile(formData);
      toast.success('Profil créé avec succès !');
      navigate(`/dashboard`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du profil');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.entity_type && formData.public_name;
      case 2:
        return formData.objective;
      case 3:
        return true; // Optional step
      case 4:
        return true; // Optional step
      case 5:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className= "min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4 ">
      <div className= "max-w-3xl mx-auto ">
        {/* Header */}
        <div className= "text-center mb-8 ">
          <Bandage className= "h-12 w-12 text-blue-500 mx-auto mb-4 " />
          <h1 className= "text-3xl font-bold text-white mb-2 ">Configuration de votre profil</h1>
          <p className= "text-gray-400 ">
            Étape {step} sur {totalSteps}
          </p>
        </div>

        {/* Progress Bar */}
        <div className= "mb-8 ">
          <div className= "h-2 bg-gray-700 rounded-full overflow-hidden ">
            <div
              className= "h-full bg-blue-600 transition-all duration-300 "
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <Card>
          <CardHeader>
            <CardTitle>
              {step === 1 && 'Qui êtes-vous ?'}
              {step === 2 && 'Quel est votre objectif ?'}
              {step === 3 && 'Quels sont les risques ?'}
              {step === 4 && 'Quelles sont vos réalisations ?'}
              {step === 5 && 'Derniers détails'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Informations de base sur votre identité'}
              {step === 2 && 'Définissez vos objectifs de réputation'}
              {step === 3 && 'Identifiez les points de vigilance'}
              {step === 4 && 'Valorisez vos accomplissements'}
              {step === 5 && 'Contraintes et ton éditorial'}
            </CardDescription>
          </CardHeader>
          <CardContent className= "space-y-6 ">
            {/* Step 1: Identity */}
            {step === 1 && (
              <div className= "space-y-6 ">
                <div className= "space-y-2 ">
                  <Label>Type d'entité *</Label>
                  <div className= "grid grid-cols-2 gap-3 ">
                    {entityTypes.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => setFormData({ ...formData, entity_type: type.value })}
                        className={`p-4 rounded-lg border-2 transition-all flex items-center gap-3 ${
                          formData.entity_type === type.value
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`entity-type-${type.value}`}
                      >
                        <type.icon className= "h-5 w-5 " />
                        <span className= "text-sm font-medium ">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className= "space-y-2 ">
                  <Label htmlFor= "public_name ">Nom public *</Label>
                  <Input
                    id= "public_name "
                    placeholder= "ex: Jean Dupont "
                    value={formData.public_name}
                    onChange={(e) => setFormData({ ...formData, public_name: e.target.value })}
                    data-testid= "public-name-input "
                  />
                </div>

                <div className= "space-y-2 ">
                  <Label>Alias / Variantes (optionnel)</Label>
                  <div className= "flex gap-2 ">
                    <Input
                      placeholder= "ex: J. Dupont, Jean D. "
                      value={tempInput}
                      onChange={(e) => setTempInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddItem('aliases', tempInput);
                        }
                      }}
                    />
                    <Button
                      type= "button "
                      onClick={() => handleAddItem('aliases', tempInput)}
                      variant= "outline "
                    >
                      Ajouter
                    </Button>
                  </div>
                  <div className= "flex flex-wrap gap-2 mt-2 ">
                    {formData.aliases.map((alias, index) => (
                      <Badge key={index} variant= "secondary " className= "cursor-pointer " onClick={() => handleRemoveItem('aliases', index)}>
                        {alias} ✕
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Objectives */}
            {step === 2 && (
              <div className= "space-y-6 ">
                <div className= "space-y-2 ">
                  <Label>Objectif principal *</Label>
                  <div className= "grid grid-cols-2 gap-2 ">
                    {objectives.map((obj) => (
                      <button
                        key={obj}
                        onClick={() => setFormData({ ...formData, objective: obj })}
                        className={`p-3 rounded-lg border text-sm transition-all ${
                          formData.objective === obj
                            ? 'border-blue-600 bg-blue-50 font-medium'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`objective-${obj.toLowerCase().replace(/\\s+/g, '-')}`}
                      >
                        {obj}
                      </button>
                    ))}
                  </div>
                </div>

                <div className= "space-y-2 ">
                  <Label>Objectifs secondaires (optionnel)</Label>
                  <div className= "flex flex-wrap gap-2 ">
                    {objectives
                      .filter((obj) => obj !== formData.objective)
                      .map((obj) => (
                        <button
                          key={obj}
                          onClick={() => {
                            if (formData.secondary_objectives.includes(obj)) {
                              setFormData({
                                ...formData,
                                secondary_objectives: formData.secondary_objectives.filter((o) => o !== obj),
                              });
                            } else {
                              setFormData({
                                ...formData,
                                secondary_objectives: [...formData.secondary_objectives, obj],
                              });
                            }
                          }}
                          className={`px-3 py-1 rounded-full text-sm border transition-all ${
                            formData.secondary_objectives.includes(obj)
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {obj}
                        </button>
                      ))}
                  </div>
                </div>

                <div className= "space-y-2 ">
                  <Label>Audiences cibles</Label>
                  <div className= "flex flex-wrap gap-2 ">
                    {audiences.map((audience) => (
                      <button
                        key={audience}
                        onClick={() => {
                          if (formData.target_audiences.includes(audience)) {
                            setFormData({
                              ...formData,
                              target_audiences: formData.target_audiences.filter((a) => a !== audience),
                            });
                          } else {
                            setFormData({
                              ...formData,
                              target_audiences: [...formData.target_audiences, audience],
                            });
                          }
                        }}
                        className={`px-3 py-1 rounded-full text-sm border transition-all ${
                          formData.target_audiences.includes(audience)
                            ? 'border-green-600 bg-green-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {audience}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Risks */}
            {step === 3 && (
              <div className= "space-y-6 ">
                <div className= "bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3 ">
                  <AlertTriangle className= "h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5 " />
                  <p className= "text-sm text-yellow-900 ">
                    Identifiez les risques potentiels : articles négatifs, controverses, homonymes, manque
                    de présence, etc. Cette information restera confidentielle.
                  </p>
                </div>

                <div className= "space-y-2 ">
                  <Label>Ajouter un risque</Label>
                  <div className= "flex gap-2 ">
                    <Textarea
                      placeholder= "Décrivez un point de vigilance... "
                      value={tempInput}
                      onChange={(e) => setTempInput(e.target.value)}
                      rows={2}
                    />
                    <Button
                      type= "button "
                      onClick={() => {
                        handleAddItem('reputation_risks', JSON.stringify({ description: tempInput, level: 'medium' }));
                      }}
                      variant= "outline "
                      className= "flex-shrink-0 "
                    >
                      Ajouter
                    </Button>
                  </div>
                </div>

                <div className= "space-y-2 ">
                  {formData.reputation_risks.map((risk, index) => {
                    const riskObj = typeof risk === 'string' ? JSON.parse(risk) : risk;
                    return (
                      <div key={index} className= "p-3 bg-red-50 border border-red-200 rounded-lg flex justify-between items-start ">
                        <p className= "text-sm text-red-900 ">{riskObj.description}</p>
                        <button
                          onClick={() => handleRemoveItem('reputation_risks', index)}
                          className= "text-red-600 hover:text-red-800 ml-2 "
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 4: Achievements */}
            {step === 4 && (
              <div className= "space-y-6 ">
                <div className= "bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3 ">
                  <Award className= "h-5 w-5 text-green-600 flex-shrink-0 mt-0.5 " />
                  <p className= "text-sm text-green-900 ">
                    Listez vos accomplissements, diplômes, missions, reconnaissances, publications. Ces
                    éléments serviront de base à votre contenu.
                  </p>
                </div>

                <div className= "space-y-2 ">
                  <Label>Ajouter une réalisation</Label>
                  <div className= "flex gap-2 ">
                    <Textarea
                      placeholder= "ex: Master en droit, Harvard 2015... "
                      value={tempInput}
                      onChange={(e) => setTempInput(e.target.value)}
                      rows={2}
                    />
                    <Button
                      type= "button "
                      onClick={() => {
                        handleAddItem('achievements', JSON.stringify({ description: tempInput, year: null }));
                      }}
                      variant= "outline "
                      className= "flex-shrink-0 "
                    >
                      Ajouter
                    </Button>
                  </div>
                </div>

                <div className= "space-y-2 ">
                  {formData.achievements.map((achievement, index) => {
                    const achObj = typeof achievement === 'string' ? JSON.parse(achievement) : achievement;
                    return (
                      <div key={index} className= "p-3 bg-blue-50 border border-blue-200 rounded-lg flex justify-between items-start ">
                        <p className= "text-sm text-blue-900 ">{achObj.description}</p>
                        <button
                          onClick={() => handleRemoveItem('achievements', index)}
                          className= "text-blue-600 hover:text-blue-800 ml-2 "
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 5: Final Details */}
            {step === 5 && (
              <div className= "space-y-6 ">
                <div className= "space-y-2 ">
                  <Label htmlFor= "tone ">Ton éditorial</Label>
                  <Input
                    id= "tone "
                    placeholder= "sobre, crédible, factuel "
                    value={formData.tone}
                    onChange={(e) => setFormData({ ...formData, tone: e.target.value })}
                    data-testid= "tone-input "
                  />
                  <p className= "text-xs text-gray-500 ">
                    Exemple : sobre et professionnel, dynamique et accessible, etc.
                  </p>
                </div>

                <div className= "space-y-2 ">
                  <Label htmlFor= "constraints ">Contraintes particulières (optionnel)</Label>
                  <Textarea
                    id= "constraints "
                    placeholder= "ex: confidentialité partielle, secteur sensible, anonymat sur certains sujets... "
                    value={formData.constraints}
                    onChange={(e) => setFormData({ ...formData, constraints: e.target.value })}
                    rows={4}
                  />
                </div>

                <div className= "bg-slate-50 border border-slate-200 rounded-lg p-4 ">
                  <h4 className= "font-medium mb-2 flex items-center gap-2 ">
                    <Check className= "h-5 w-5 text-green-600 " />
                    Récapitulatif de votre profil
                  </h4>
                  <dl className= "space-y-2 text-sm ">
                    <div className= "flex gap-2 ">
                      <dt className= "font-medium text-gray-600 w-32 ">Type :</dt>
                      <dd>{formData.entity_type}</dd>
                    </div>
                    <div className= "flex gap-2 ">
                      <dt className= "font-medium text-gray-600 w-32 ">Nom :</dt>
                      <dd>{formData.public_name}</dd>
                    </div>
                    <div className= "flex gap-2 ">
                      <dt className= "font-medium text-gray-600 w-32 ">Objectif :</dt>
                      <dd>{formData.objective}</dd>
                    </div>
                    <div className= "flex gap-2 ">
                      <dt className= "font-medium text-gray-600 w-32 ">Risques :</dt>
                      <dd>{formData.reputation_risks.length} identifiés</dd>
                    </div>
                    <div className= "flex gap-2 ">
                      <dt className= "font-medium text-gray-600 w-32 ">Réalisations :</dt>
                      <dd>{formData.achievements.length} listées</dd>
                    </div>
                  </dl>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className= "flex justify-between pt-6 border-t ">
              <Button variant= "outline " onClick={handleBack} disabled={step === 1}>
                <ArrowLeft className= "h-4 w-4 mr-2 " />
                Retour
              </Button>

              {step < totalSteps ? (
                <Button onClick={handleNext} disabled={!canProceed()} data-testid= "next-step-btn ">
                  Suivant
                  <ArrowRight className= "h-4 w-4 ml-2 " />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={loading || !canProceed()} data-testid= "submit-profile-btn ">
                  {loading ? 'Création...' : 'Créer mon profil'}
                  <Check className= "h-4 w-4 ml-2 " />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Onboarding;
